// ── Overview / welcome section (Wave 9, REAL own-scoped reads) ──────────────
// The participant's welcome: own name (from /auth/me), own cohort + program
// context (from the server-scoped cohorts read, scope=mine), and coach where
// surfaced. ISOLATION: this reads ONLY the participant's OWN profile + OWN
// cohorts — never a roster, never a peer, never another cohort. REPORT-EXCLUSION:
// no report content/status/existence is shown. Counts are LIVE (own-cohort list
// length); a non-ready read shows "—", never a fabricated number.

import { Badge, Card, IconChart, StatTile } from "@vjal/ui";

import { useHonestQuery } from "../../components/honest-state";
import type {
  CoachingCohortDto,
  CoachingProfileDto,
  ListResponse,
} from "../../services/coaching-dtos";
import { cohortStatusLabel, cohortStatusTone } from "../cohorts/cohort-detail-helpers";
import { ParticipantSectionShell } from "./participant-section-shell";
import {
  orDash,
  ownDisplayName,
  participantQueryKeys,
  readOwnCohorts,
  readOwnProfile,
} from "./participant-dashboard-helpers";

export function ParticipantOverviewSection() {
  const profileQuery = useHonestQuery<CoachingProfileDto>(
    participantQueryKeys.profile(),
    readOwnProfile
  );
  const cohortsQuery = useHonestQuery<ListResponse<CoachingCohortDto>>(
    participantQueryKeys.ownCohorts(),
    readOwnCohorts
  );

  const profile = profileQuery.data;
  const cohorts = cohortsQuery.data?.items ?? [];

  // Own-data-only KPI signal: counts derived strictly from the participant's OWN
  // cohort list (never a roster, never a report). Only a ready-WITH-DATA read
  // shows a count; loading shimmers and any non-ready/empty read shows the honest
  // "—" posture — never a fabricated (or bare) number on a fresh learner.
  const cohortsReadyWithData = cohortsQuery.posture === null && cohorts.length > 0;
  const cohortsLoading = cohortsQuery.posture === "loading";
  const cohortsEmptyPosture = !cohortsLoading && !cohortsReadyWithData;
  const programCount = new Set(
    cohorts.map((cohort) => cohort.program_name).filter((name): name is string => Boolean(name))
  ).size;

  // The welcome card is driven by the OWN profile read; surface ITS posture
  // first (a not-ready profile is loading/unavailable, never a blank greeting).
  return (
    <ParticipantSectionShell
      title={`Welcome${profile ? `, ${ownDisplayName(profile)}` : ""}`}
      titleExactName="Welcome"
      subtitle="Your coaching home — your cohorts, sessions, and progress, all in one place."
      posture={profileQuery.posture}
      gate={profileQuery.gate}
      reference={profileQuery.reference}
      onRetry={profileQuery.retry}
      icon={<IconChart size={22} />}
      testId="participant-overview-section"
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2" data-testid="participant-overview-stats">
          <StatTile
            label="Your cohorts"
            value={String(cohorts.length)}
            meta="Active learning paths"
            loading={cohortsLoading}
            empty={cohortsEmptyPosture}
            emptyLabel="Will populate once you're enrolled"
          />
          <StatTile
            label="Your programs"
            value={String(programCount)}
            meta="Across your cohorts"
            loading={cohortsLoading}
            empty={cohortsEmptyPosture}
            emptyLabel="Will populate once you're enrolled"
          />
        </div>
        <Card className="shadow-none">
          <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
            <Field label="Name">{orDash(profile?.name)}</Field>
            <Field label="Your cohorts">
              {cohortsQuery.posture === null || cohortsQuery.posture === "empty"
                ? String(cohorts.length)
                : "—"}
            </Field>
          </div>
        </Card>

        {cohorts.length > 0 ? (
          <div className="space-y-3" data-testid="participant-overview-cohorts">
            {cohorts.map((cohort) => (
              <Card key={cohort.id} className="shadow-none">
                <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                  <Field label="Cohort">{orDash(cohort.name)}</Field>
                  <Field label="Status">
                    <Badge variant={cohortStatusTone(cohort.status)}>{cohortStatusLabel(cohort.status)}</Badge>
                  </Field>
                  <Field label="Program">{orDash(cohort.program_name)}</Field>
                  <Field label="Coach">{orDash(cohort.coach_name)}</Field>
                </div>
              </Card>
            ))}
          </div>
        ) : cohortsQuery.posture === null ? (
          <p className="m-0 text-sm text-ink-muted">You're not enrolled in a cohort yet.</p>
        ) : null}
      </div>
    </ParticipantSectionShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{label}</span>
      <p className="m-0 text-sm text-strong">{children}</p>
    </div>
  );
}
